/**
 * useFileUpload — manages file selection, upload progress, and conversion dialog.
 * Group C: File Selection & Upload (7 state variables)
 *
 * extractThumbnail is exported as a pure utility (no state dependencies).
 * handleConversionConfirm/Cancel stay in PrimateVision.tsx because they cross
 * into useVideoLibrary state (videoQueue, activeVideoId).
 */
import { useState, useEffect } from 'react';

export interface UseFileUploadReturn {
  selectedFile: File | null;
  setSelectedFile: (file: File | null) => void;
  selectedDemoVideo: string | null;
  setSelectedDemoVideo: (id: string | null) => void;
  conversionFile: File | null;
  setConversionFile: (file: File | null) => void;
  showConversionDialog: boolean;
  setShowConversionDialog: (show: boolean) => void;
  fileError: string | null;
  setFileError: (error: string | null) => void;
  uploadProgress: number;
  setUploadProgress: (pct: number | ((prev: number) => number)) => void;
  uploadPath: 'checking' | 'uploading' | null;
  setUploadPath: (path: 'checking' | 'uploading' | null) => void;
  localVideoUrl: string | null;
  setLocalVideoUrl: (url: string | null) => void;
}

export function useFileUpload(): UseFileUploadReturn {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDemoVideo, setSelectedDemoVideo] = useState<string | null>(null);
  const [conversionFile, setConversionFile] = useState<File | null>(null);
  const [showConversionDialog, setShowConversionDialog] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPath, setUploadPath] = useState<'checking' | 'uploading' | null>(null);
  const [localVideoUrl, setLocalVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalVideoUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setLocalVideoUrl(null);
    }
  }, [selectedFile]);

  return {
    selectedFile,
    setSelectedFile,
    selectedDemoVideo,
    setSelectedDemoVideo,
    conversionFile,
    setConversionFile,
    showConversionDialog,
    setShowConversionDialog,
    fileError,
    setFileError,
    uploadProgress,
    setUploadProgress: setUploadProgress as UseFileUploadReturn['setUploadProgress'],
    uploadPath,
    setUploadPath,
    localVideoUrl,
    setLocalVideoUrl,
  };
}

/**
 * extractThumbnail — captures a frame from a video file as a data URL.
 * Pure utility: no React state, safe to call from any context.
 */
export async function extractThumbnail(file: File): Promise<string> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let resolved = false;

    function captureFrame() {
      if (resolved) return;
      try {
        const canvas = document.createElement('canvas');
        // Preserve native aspect ratio — don't squash portrait/square videos
        const aspectRatio = video.videoWidth > 0 && video.videoHeight > 0
          ? video.videoWidth / video.videoHeight
          : 16 / 9;
        canvas.width = 160;
        canvas.height = Math.round(160 / aspectRatio);
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        // Check if frame is non-black by sampling a pixel
        const px = ctx.getImageData(80, 45, 1, 1).data;
        if (px[0] === 0 && px[1] === 0 && px[2] === 0) {
          // Black frame — try a bit later
          return;
        }
        resolved = true;
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch {
        // ignore, will retry
      }
    }

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    video.src = url;
    video.load();

    video.onloadedmetadata = () => {
      // Seek to 10% or 1s, whichever is earlier
      video.currentTime = Math.min(Math.max(video.duration * 0.1, 0.5), 3);
    };

    video.onseeked = () => {
      // Try capturing immediately, then retry a few times if black
      let attempts = 0;
      const tryCapture = () => {
        captureFrame();
        if (!resolved && attempts++ < 5) {
          setTimeout(tryCapture, 200);
        } else if (!resolved) {
          // Give up — grey placeholder
          resolved = true;
          URL.revokeObjectURL(url);
          const c = document.createElement('canvas');
          c.width = 160; c.height = 90;
          const cx = c.getContext('2d')!;
          cx.fillStyle = '#1E2028';
          cx.fillRect(0, 0, 160, 90);
          // Draw a play icon
          cx.fillStyle = '#9BA4B5';
          cx.beginPath();
          cx.moveTo(60, 30); cx.lineTo(60, 60); cx.lineTo(105, 45);
          cx.closePath(); cx.fill();
          resolve(c.toDataURL());
        }
      };
      setTimeout(tryCapture, 50);
    };

    video.onerror = () => {
      if (resolved) return;
      resolved = true;
      URL.revokeObjectURL(url);
      const c = document.createElement('canvas');
      c.width = 160; c.height = 90;
      const cx = c.getContext('2d')!;
      cx.fillStyle = '#1E2028';
      cx.fillRect(0, 0, 160, 90);
      cx.fillStyle = '#9BA4B5';
      cx.beginPath();
      cx.moveTo(60, 30); cx.lineTo(60, 60); cx.lineTo(105, 45);
      cx.closePath(); cx.fill();
      resolve(c.toDataURL());
    };

    // Timeout safety net
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        URL.revokeObjectURL(url);
        const c = document.createElement('canvas');
        c.width = 160; c.height = 90;
        const cx = c.getContext('2d')!;
        cx.fillStyle = '#1E2028';
        cx.fillRect(0, 0, 160, 90);
        cx.fillStyle = '#9BA4B5';
        cx.beginPath();
        cx.moveTo(60, 30); cx.lineTo(60, 60); cx.lineTo(105, 45);
        cx.closePath(); cx.fill();
        resolve(c.toDataURL());
      }
    }, 8000);
  });
}
